import axios from "./axios";

// Journaling (standalone documentation journals + rich-text entries).
// Backend: /api/v1/journaling/journals and nested /entries.

export async function fetchJournals(params = {}) {
  return axios({ method: "get", url: "/journaling/journals", params })
    .then((response) => response.data)
    .catch((error) => error.response?.data);
}

export async function fetchJournal(id) {
  return axios({ method: "get", url: `/journaling/journals/${id}` })
    .then((response) => response.data)
    .catch((error) => error.response?.data);
}

export async function createJournal(payload) {
  return axios({
    method: "post",
    url: "/journaling/journals",
    data: { journal: payload },
  })
    .then((response) => response.data)
    .catch((error) => error.response?.data);
}

export async function updateJournal(id, payload) {
  return axios({
    method: "put",
    url: `/journaling/journals/${id}`,
    data: { journal: payload },
  })
    .then((response) => response.data)
    .catch((error) => error.response?.data);
}

export async function deleteJournal(id) {
  return axios({ method: "delete", url: `/journaling/journals/${id}` })
    .then((response) => response.data)
    .catch((error) => error.response?.data);
}

export async function archiveJournal(id) {
  return axios({ method: "put", url: `/journaling/journals/${id}/archive` })
    .then((response) => response.data)
    .catch((error) => error.response?.data);
}

export async function unarchiveJournal(id) {
  return axios({ method: "put", url: `/journaling/journals/${id}/unarchive` })
    .then((response) => response.data)
    .catch((error) => error.response?.data);
}

export async function fetchJournalPdf(id) {
  let response;
  try {
    response = await axios({
      method: "get",
      url: `/journaling/journals/${id}/pdf`,
      responseType: "blob",
    });
  } catch (error) {
    const json = await parsePdfErrorBlob(error?.response?.data);
    if (json) {
      const next = new Error(
        (Array.isArray(json.errors) ? json.errors.join(", ") : null) || "pdf_failed"
      );
      next.errors = json.errors;
      throw next;
    }
    throw error;
  }

  const blob = response.data;
  if (blob instanceof Blob) {
    const head = (await readBlobText(blob.slice(0, 8))).slice(0, 5);
    if (head.startsWith("{") || head.startsWith("<")) {
      const json = await parsePdfErrorBlob(blob);
      const next = new Error(
        (Array.isArray(json?.errors) ? json.errors.join(", ") : null) || "pdf_failed"
      );
      next.errors = json?.errors;
      throw next;
    }
  }

  return { blob, filename: "journal.pdf" };
}

async function parsePdfErrorBlob(blob) {
  if (!blob) return null;
  if (typeof blob === "object" && !("size" in blob) && (blob.errors || blob.problem)) {
    return blob;
  }
  try {
    return JSON.parse(await readBlobText(blob));
  } catch (_e) {
    return null;
  }
}

function readBlobText(blob) {
  if (!blob) return Promise.resolve("");
  if (typeof blob.text === "function") return blob.text();
  return new Promise((resolve, reject) => {
    if (typeof FileReader === "undefined") {
      resolve("");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

export async function sendJournal(id, payload) {
  return axios({
    method: "post",
    url: `/journaling/journals/${id}/send_email`,
    data: payload,
  })
    .then((response) => response.data)
    .catch((error) => error.response?.data);
}

export async function fetchJournalEntries(journalId) {
  return axios({
    method: "get",
    url: `/journaling/journals/${journalId}/entries`,
  })
    .then((response) => response.data)
    .catch((error) => error.response?.data);
}

export async function createJournalEntry(journalId, payload) {
  return axios({
    method: "post",
    url: `/journaling/journals/${journalId}/entries`,
    data: { entry: payload },
  })
    .then((response) => response.data)
    .catch((error) => error.response?.data);
}

export async function updateJournalEntry(journalId, entryId, payload) {
  return axios({
    method: "put",
    url: `/journaling/journals/${journalId}/entries/${entryId}`,
    data: { entry: payload },
  })
    .then((response) => response.data)
    .catch((error) => error.response?.data);
}

export async function deleteJournalEntry(journalId, entryId) {
  return axios({
    method: "delete",
    url: `/journaling/journals/${journalId}/entries/${entryId}`,
  })
    .then((response) => response.data)
    .catch((error) => error.response?.data);
}
